/**
 * Reusable Input component for trAInedup.ai
 * Provides consistent styling for form inputs
 */

import React, { useId } from 'react';
import { LucideIcon } from 'lucide-react';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  inputSize?: 'sm' | 'md' | 'lg';
  rightAction?: React.ReactNode;
}

/**
 * Input component with label, error states, and icon support
 * Follows trAInedup.ai design system
 */
export const Input: React.FC<InputProps> = ({
  label,
  error,
  helperText,
  icon: Icon,
  iconPosition = 'left',
  fullWidth = false,
  inputSize = 'md',
  rightAction,
  className = '',
  id,
  ...props
}) => {
  const reactId = useId();
  // Use stable id for hydration-safe markup
  const inputId = id || `input-${reactId}`;

  // Size-based classes
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base', 
    lg: 'px-5 py-4 text-lg',
  };

  // Base input classes
  const baseInputClasses = 'transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

  // Input styling classes
  const inputClasses = `
    ${baseInputClasses}
    ${error ? 'border-red-500 focus:ring-red-500' : 'border-neutral-300'}
    ${Icon ? (iconPosition === 'left' ? 'pl-10' : 'pr-10') : ''}
    ${rightAction ? 'pr-10' : ''}
    ${fullWidth ? 'w-full' : ''}
    ${sizeClasses[inputSize]}
    border rounded-lg bg-white text-neutral-800 placeholder-neutral-400
    ${className}
  `.trim();

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {/* Label */}
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-neutral-700 mb-2"
        >
          {label}
        </label>
      )}

      {/* Input Container */}
      <div className="relative">
        {/* Left Icon */}
        {Icon && iconPosition === 'left' && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-neutral-400" />
          </div>
        )}

        {/* Input Field */}
        <input
          id={inputId}
          className={inputClasses}
          {...props}
        />

        {/* Right Icon */}
        {Icon && iconPosition === 'right' && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-neutral-400" />
          </div>
        )}

        {/* Right Action (interactive button) */}
        {rightAction && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {rightAction}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}

      {/* Helper Text */}
      {helperText && !error && (
        <p className="mt-2 text-sm text-neutral-500">
          {helperText}
        </p>
      )}
    </div>
  );
};
